import { globalLoggerToken } from "@/tokens";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import type { CustomizationData, WidgetConfig } from "./types";

@singleton()
class CustomizationManager {
    constructor(
        @inject(globalLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    private getCustomizations(
        config: WidgetConfig
    ): Array<Partial<CustomizationData>> | [] {
        if (!window.gw || !window.gw.customizations) return [];

        const customizations: Array<CustomizationData> = [];

        window.gw.customizations.forEach(([selectWidget, customizeWidget]) => {
            if (selectWidget(config)) {
                customizations.push(customizeWidget(config));

                this.logger.debug("CustomizationManager.getCustomization", {
                    customizations,
                });
            }
        });

        return customizations;
    }

    public injectStylesFromDesignMode({
        element,
        shadowRoot,
        scriptSelector,
    }: {
        element: Element;
        shadowRoot: ShadowRoot | null;
        scriptSelector: string;
    }): void {
        const scriptElement =
            element.parentElement?.querySelector(scriptSelector);

        if (!scriptElement) return;

        const styleElement = document.createElement("style");
        styleElement.textContent = scriptElement.textContent;

        (shadowRoot || element).appendChild(styleElement);

        scriptElement.remove();

        this.logger.debug(
            "CustomizationManager.injectStylesFromDesignMode",
            styleElement
        );
    }

    public injectStylesFromCustomization(config: WidgetConfig): void {
        const customizations = this.getCustomizations(config);
        if (customizations.length === 0) return;

        customizations.forEach((customization) => {
            if (!customization.styleElement) return;

            if (config.shadowRoot) {
                config.shadowRoot.appendChild(
                    customization.styleElement.cloneNode(true)
                );

                this.logger.debug(
                    "CustomizationManager.injectStyles",
                    customization.styleElement
                );
            }
        });
    }

    public async beforeWidgetInject(config: WidgetConfig): Promise<void> {
        const customizations = this.getCustomizations(config);
        if (customizations.length === 0) return;

        for (const customization of customizations) {
            const callback = customization.beforeWidgetInject;

            if (!callback) return;

            if (callback instanceof Promise) {
                await callback();
            } else {
                void callback();
            }
            this.logger.debug("CustomizationManager.beforeWidgetInject");
        }
    }

    public afterWidgetInject(config: WidgetConfig): void {
        const customizations = this.getCustomizations(config);
        if (customizations.length === 0) return;

        customizations.forEach((customization) => {
            const callback = customization.afterWidgetInject;

            if (!callback) return;

            callback();
        });

        this.logger.debug("CustomizationManager.afterWidgetInject");
    }
}

export default CustomizationManager;
